import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../stores/Stores";
import { Button } from "@fluentui/react-components";
import { EditRegular, AddRegular, ChatRegular, CalendarRegular } from "@fluentui/react-icons";
import DateField from "../../form/dateField/DateField";
import TextField from "../../form/textField/TextField";
import CheckBox from "../../form/checkbox/CheckBox";
import styles from "../customerMeetings.module.scss";
import { BusinessLine } from "../../shared/types/BusinessLine";
import {
    validateAttendeeEmails,
    mapArrayToString,
    validateTitle,
    mapStringToArray,
    validateMeetingEndingDate,
} from "../MeetingHelpers";
import { FormActionType } from "./FormActionType";
import { CustomerMeeting } from "../store/CustomerMeeting";
import { useTranslation } from "../../shared/translation/Translation";
import { LanguageVersion } from "../../shared/types/LanguageVersion";
import { getMeetingDefaultEndTime, getMeetingMinTime } from "../../shared/utils/dateUtils";
import { clearSeconds } from "../../shared/utils/dateUtils";
import CustomersField, { CustomersFieldRef } from "../../form/customerField/CustomersField";
import { Customer, ICustomer } from "../../models/Customer";
import { MeetingKind } from "../../shared/types/MeetingKind";
import PersonsField from "../../form/personField/PersonsField";
import { IPerson } from "../../models/Person";
import Section from "../../form/section/Section";
import EmployeesAddAndEditField from "../../form/employeesAddAndEditField/EmployeesAddAndEditField";

interface RetailOrPrivateMeetingFormProps {
    formActionType: FormActionType;
    businessLine: BusinessLine;
    meeting?: CustomerMeeting;

    onClose: () => void;
    onSuccess?: () => void;
}

const RetailMeetingForm: React.FC<RetailOrPrivateMeetingFormProps> = observer(
    (props: RetailOrPrivateMeetingFormProps) => {
        const { meetingStore, userStore } = useStore();
        const messages = useTranslation();

        const [titleText, setTitleText] = useState<string>("");
        const [additionalAttendeesEmailsText, setAdditionalAttendeesEmailsText] = useState<string>("");
        const [startingDate, setStartingDate] = useState(new Date());
        const [endingDate, setEndingDate] = useState(new Date());
        const [isEnglishCommunicationChecked, setIsEnglishCommunicationChecked] = useState<boolean>(false);
        const [isVirtualAppointment, setIsVirtualAppointment] = useState<boolean>(false);

        const [newAdditionalAttendeesEmailsText, setNewAdditionalAttendeesEmailsText] = useState<string>("");
        const [isLoading, setIsLoading] = useState(true);
        const [customers, setCustomers] = useState<ICustomer[]>([]);
        const [externalConsultants, setExternalConsultants] = useState<IPerson[]>([]);
        const [thirdParties, setThridParties] = useState<IPerson[]>([]);
        const [
            isAdditionalAttendeesFieldRequestedExpanded,
            setIsAdditionalAttendeesFieldRequestedExpanded,
        ] = useState<boolean>(false);

        const isFormTypeUpdate = props.formActionType === FormActionType.UpdateMeeting;

        const isCorporateMeeting = false;

        const customersRef = useRef<CustomersFieldRef>(null);
        const externalConsultantsRef = useRef<CustomersFieldRef>(null);
        const thridPartiesRef = useRef<CustomersFieldRef>(null);

        useEffect(() => {
            if (isFormTypeUpdate) {
                setTitleText(props.meeting!.subject);
                setCustomers(props.meeting!.customers);
                setExternalConsultants(props.meeting!.externalConsultants ?? []);
                setThridParties(props.meeting!.thirdParties ?? []);
                setStartingDate(props.meeting!.startAt);
                setEndingDate(props.meeting!.endAt);
                setIsEnglishCommunicationChecked(props.meeting!.languageVersion === LanguageVersion.English);
                setIsVirtualAppointment(props.meeting?.meetingKind === MeetingKind.VirtualAppointment);

                if (props.meeting!.additionalAttendeesEmails) {
                    setAdditionalAttendeesEmailsText(mapArrayToString(props.meeting!.additionalAttendeesEmails!));
                }
            } else {
                const defaultStartingDate = getMeetingMinTime(new Date());
                setStartingDate(defaultStartingDate);
                setEndingDate(getMeetingDefaultEndTime(defaultStartingDate));
                setIsVirtualAppointment(
                    meetingStore.meetingOptions.defaultMeetingKind === MeetingKind.VirtualAppointment ? true : false
                );
                setCustomers([Customer.New()]);
            }
            setIsLoading(false);
        }, []);

        const validateAllFields = () => {
            if (isFormTypeUpdate) {
                return (
                    validateAttendeeEmails(newAdditionalAttendeesEmailsText) &&
                    validateMeetingEndingDate(endingDate, maxDate) &&
                    customers
                        .filter((customer) => customer.isNew)
                        .every((customer) => customer.validateCustomer(isCorporateMeeting).isValid) &&
                    externalConsultants
                        .filter((consultant) => consultant.isNew)
                        .every((consultant) => consultant.validatePerson().isValid) &&
                    thirdParties
                        .filter((thirdParty) => thirdParty.isNew)
                        .every((thirdParty) => thirdParty.validatePerson().isValid)
                );
            }

            return (
                titleText.length > 0 &&
                validateTitle(titleText) &&
                validateAttendeeEmails(additionalAttendeesEmailsText) &&
                validateMeetingEndingDate(endingDate, maxDate) &&
                customers.length > 0 &&
                customers.every((customer) => customer.validateCustomer(isCorporateMeeting).isValid) &&
                externalConsultants.every((i) => i.validatePerson().isValid) &&
                thirdParties.every((i) => i.validatePerson().isValid)
            );
        };

        const createMeeting = async () => {
            await meetingStore.createMeeting({
                subject: titleText,
                customers: customers,
                startAt: clearSeconds(startingDate),
                endAt: clearSeconds(endingDate),
                additionalAttendeesEmails: mapStringToArray(additionalAttendeesEmailsText),
                businessLine: props.businessLine,
                languageVersion: isEnglishCommunicationChecked ? LanguageVersion.English : userStore.languageVersion,
                businessLineLanguageVersion: userStore.languageVersion,
                meetingKind: isVirtualAppointment ? MeetingKind.VirtualAppointment : MeetingKind.MsTeams,
                externalConsultants: externalConsultants,
                thirdParties: thirdParties,
            });

            if (!meetingStore.createMeetingRequestStatus.isPending) {
                props.onClose();
            }
        };

        const updateMeeting = async () => {
            await meetingStore.updateMeeting({
                urlHash: props.meeting!.urlHash,
                customers: customers.filter((customer) => customer.isNew),
                startAt: clearSeconds(startingDate),
                endAt: clearSeconds(endingDate),
                additionalAttendeesEmails: mapStringToArray(newAdditionalAttendeesEmailsText),
                externalConsultants: externalConsultants.filter((i) => i.isNew),
                thirdParties: thirdParties.filter((i) => i.isNew),
            });

            if (meetingStore.updateMeetingRequestStatus.isDone) {
                props.onSuccess!();
            } else if (!meetingStore.updateMeetingRequestStatus.isPending) {
                props.onClose();
            }
        };

        const isShowAdditionalAttendees = (): boolean => {
            return (
                isAdditionalAttendeesFieldRequestedExpanded ||
                additionalAttendeesEmailsText.length > 0 ||
                newAdditionalAttendeesEmailsText.length > 0
            );
        };

        if (isLoading) {
            return null;
        }

        const minDate = new Date();
        const maxDate = new Date(minDate);
        maxDate.setDate(maxDate.getDate() + meetingStore.meetingOptions.maxNumberOfDaysToPlanMeeting);

        return (
            <>
                <TextField
                    text={titleText}
                    handleDataChange={setTitleText}
                    required={true}
                    placeholder={messages.meetingModals.titlePlaceholder}
                    iconComponent={<EditRegular />}
                    showSuccessIndicator={!!titleText.length && validateTitle(titleText)}
                    isInvalid={validateTitle(titleText) === false}
                    errorMessage={messages.meetingModals.validation.invalidTitle}
                    disabled={isFormTypeUpdate}
                />

                <Section
                    headerMessage={messages.meetingModals.customers}
                    tooltipMessage={messages.meetingModals.customersTooltip}
                    body={
                        <CustomersField
                            ref={customersRef}
                            customers={customers}
                            handleDataChange={setCustomers}
                            isFormTypeUpdate={isFormTypeUpdate}
                            isCorporateMeeting={isCorporateMeeting}
                        />
                    }
                    headerNode={
                        <Button
                            icon={<AddRegular />}
                            appearance="transparent"
                            onClick={() => {
                                customersRef.current?.addNew();
                            }}
                        >
                            {isCorporateMeeting
                                ? messages.meetingModals.corporate.addContactPerson
                                : messages.meetingModals.addClient}
                        </Button>
                    }
                />

                <Section
                    headerMessage={messages.meetingModals.consultants}
                    tooltipMessage={messages.meetingModals.consultantsToolip}
                    body={
                        <EmployeesAddAndEditField
                            isFormTypeUpdate={isFormTypeUpdate}
                            additionalAttendeesEmailsText={additionalAttendeesEmailsText
                                .split(";")
                                .filter((item) => item.trim() !== "")}
                            newAdditionalAttendeesEmailsText={newAdditionalAttendeesEmailsText
                                .split(";")
                                .filter((item) => item.trim() !== "")}
                            onAdditionalAttendeesEmailsDataChange={(emails) => {
                                setAdditionalAttendeesEmailsText(emails.join(";"));
                            }}
                            onNewAdditionalAttendeesEmailsDataChange={(emails) => {
                                setNewAdditionalAttendeesEmailsText(emails.join(";"));
                            }}
                            isExpanded={isShowAdditionalAttendees()}
                        />
                    }
                    headerNode={
                        isShowAdditionalAttendees() === false && (
                            <Button
                                icon={<AddRegular />}
                                appearance="transparent"
                                onClick={() => {
                                    setIsAdditionalAttendeesFieldRequestedExpanded(true);
                                }}
                            >
                                {messages.meetingModals.add}
                            </Button>
                        )
                    }
                />

                <Section
                    headerMessage={messages.meetingModals.externalConsultants}
                    tooltipMessage={messages.meetingModals.externalConsultantsTooltip}
                    body={
                        <PersonsField
                            ref={externalConsultantsRef}
                            persons={externalConsultants}
                            handleDataChange={setExternalConsultants}
                            isFormTypeUpdate={isFormTypeUpdate}
                        />
                    }
                    headerNode={
                        <Button
                            icon={<AddRegular />}
                            appearance="transparent"
                            onClick={() => {
                                externalConsultantsRef.current?.addNew();
                            }}
                        >
                            {messages.meetingModals.add}
                        </Button>
                    }
                />

                <Section
                    headerMessage={messages.meetingModals.thirdParties}
                    tooltipMessage={messages.meetingModals.thirdPartiesTooltip}
                    body={
                        <PersonsField
                            ref={thridPartiesRef}
                            persons={thirdParties}
                            handleDataChange={setThridParties}
                            isFormTypeUpdate={isFormTypeUpdate}
                        />
                    }
                    headerNode={
                        <Button
                            icon={<AddRegular />}
                            appearance="transparent"
                            onClick={() => {
                                thridPartiesRef.current?.addNew();
                            }}
                        >
                            {messages.meetingModals.add}
                        </Button>
                    }
                />

                <CheckBox
                    label={messages.meetingModals.englishCommunicationCheckboxLabel}
                    toggle
                    isChecked={isEnglishCommunicationChecked}
                    iconComponent={<ChatRegular />}
                    handleDataChange={setIsEnglishCommunicationChecked}
                    disabled={isFormTypeUpdate}
                />
                <DateField
                    startingDate={startingDate}
                    setStartingDate={setStartingDate}
                    endingDate={endingDate}
                    setEndingDate={setEndingDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    isInvalid={!validateMeetingEndingDate(endingDate, maxDate)}
                    errorMessage={messages.meetingModals.validation.invalidMeetingEndingDate(
                        meetingStore.meetingOptions.maxNumberOfDaysToPlanMeeting
                    )}
                />
                {meetingStore.meetingOptions.showMeetingKindToggle ? (
                    <CheckBox
                        label={messages.meetingModals.useVirtualAppointments}
                        toggle
                        iconComponent={<CalendarRegular />}
                        isChecked={isVirtualAppointment}
                        handleDataChange={setIsVirtualAppointment}
                        disabled={isFormTypeUpdate}
                    />
                ) : (
                    <></>
                )}
                <div className={styles.buttonRow}>
                    <Button
                        disabled={
                            meetingStore.createMeetingRequestStatus.isPending ||
                            meetingStore.updateMeetingRequestStatus.isPending ||
                            !validateAllFields()
                        }
                        appearance="primary"
                        onClick={isFormTypeUpdate ? updateMeeting : createMeeting}
                    >
                        {isFormTypeUpdate ? messages.updateMeeting.updateButton : messages.createMeeting.createButton}
                    </Button>
                    <Button onClick={props.onClose}>{messages.meetingModals.closeButton}</Button>
                </div>
            </>
        );
    }
);

export default RetailMeetingForm;
